import { z } from 'zod';

export const membershipRoleSchema = z.enum(['owner', 'member']);
export type MembershipRole = z.infer<typeof membershipRoleSchema>;

export const orgSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  ownerId: z.string(),
  createdBy: z.string(),
  defaultClientId: z.string().optional(),
  isDefault: z.boolean().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type Org = z.infer<typeof orgSchema>;

export const orgMembershipSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  role: membershipRoleSchema,
  invitedBy: z.string().optional(),
  invitedAt: z.string().datetime().optional(),
  acceptedAt: z.string().datetime().optional(),
  status: z.enum(['pending', 'active', 'revoked']).default('active'),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type OrgMembership = z.infer<typeof orgMembershipSchema>;

export const brandAssetSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  type: z.enum(['logo', 'font', 'ref_image']),
  name: z.string().optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  url: z.string().url().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type BrandAsset = z.infer<typeof brandAssetSchema>;

export const brandKitSchema = z.object({
  id: z.string(),
  clientId: z.string().optional(),
  clientIds: z.array(z.string()).default([]),
  name: z.string(),
  colors: z.array(z.string()).default([]),
  fonts: z.array(z.string()).default([]),
  logos: z.array(z.string()).default([]),
  tone: z.string().optional(),
  watermark: z.string().optional(),
  constraints: z
    .object({
      logoPosition: z.enum(['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center']).optional(),
      safeColorsOnly: z.boolean().optional(),
      textLanguage: z.enum(['en', 'ar', 'both']).optional()
    })
    .optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type BrandKit = z.infer<typeof brandKitSchema>;

export const clientSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  name: z.string(),
  slug: z.string().optional(),
  ownerId: z.string(),
  createdBy: z.string(),
  defaultBrandKitId: z.string().optional(),
  isDefault: z.boolean().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type Client = z.infer<typeof clientSchema>;

export const projectStatusSchema = z.enum(['active', 'archived']);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

export const projectSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  clientId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  status: projectStatusSchema.default('active'),
  defaultAspect: z.string().optional(),
  defaultLang: z.enum(['en', 'ar', 'both']).optional(),
  brandKitId: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type Project = z.infer<typeof projectSchema>;

export const inviteTokenSchema = z.object({
  id: z.string(),
  orgId: z.string().optional(),
  userId: z.string().optional(),
  scope: z.enum(['org']).default('org'),
  email: z.string().email(),
  role: membershipRoleSchema.default('member'),
  expiresAt: z.string().datetime(),
  status: z.enum(['pending', 'accepted', 'expired', 'revoked']).default('pending'),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});
export type InviteToken = z.infer<typeof inviteTokenSchema>;
